'use client';

import React from 'react';
import { Box, Typography, Button, IconButton, Tooltip } from '@mui/material';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import CompressIcon from '@mui/icons-material/Compress';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import TextareaCodeEditor from './TextareaCodeEditor';

interface EditorPanelProps {
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  onExpand?: () => void;
  onFileDrop?: (value: string, fileName?: string) => void;
  onFileDropError?: (fileName?: string) => void;
  placeholder?: string;
  toolbarActions?: React.ReactNode;
  ariaLabel?: string;
  inactive?: boolean;
  inactiveHint?: string;
  tinted?: boolean;
  readOnly?: boolean;
  isDarkMode: boolean;
  forceDarkEditor?: boolean;
  forceDarkerBackground?: boolean;
  height?: number | string;
}

const EditorPanel = ({
  label,
  value,
  onChange,
  onExpand,
  onFileDrop,
  onFileDropError,
  placeholder = '',
  toolbarActions,
  ariaLabel,
  inactive = false,
  inactiveHint,
  tinted = false,
  readOnly = false,
  isDarkMode,
  forceDarkEditor = false,
  forceDarkerBackground = false,
  height = '400px'
}: EditorPanelProps) => {
  const handleFormat = () => {
    try {
      const obj = JSON.parse(value);
      onChange(JSON.stringify(obj, null, 2));
    } catch (e) {
      // If invalid JSON, ignore format request
    }
  };

  const handleMinify = () => {
    try {
      const obj = JSON.parse(value);
      onChange(JSON.stringify(obj));
    } catch (e) {
      // If invalid JSON, ignore minify request
    }
  };

  const [isDragOver, setIsDragOver] = React.useState(false);
  const acceptsDrops = Boolean(onFileDrop) && !readOnly;

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!acceptsDrops || !onFileDrop) return;

    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];

    if (!file || (!file.name.endsWith('.json') && file.type !== 'application/json')) {
      onFileDropError?.(file?.name);
      return;
    }

    onFileDrop(await file.text(), file.name);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (acceptsDrops) {
      event.preventDefault();
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => setIsDragOver(false);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Box className="editor-panel-header">
        <Typography variant="subtitle2" component="h2" className="editor-panel-label">
          {label}
        </Typography>
        <Box className="editor-panel-actions">
          {toolbarActions}
          {!readOnly && (
            <>
              <Tooltip title="Format JSON" arrow>
                <IconButton
                  size="small"
                  onClick={handleFormat}
                  className="editor-panel-icon-button"
                  aria-label="Format JSON"
                >
                  <FormatAlignCenterIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Minify JSON" arrow>
                <IconButton
                  size="small"
                  onClick={handleMinify}
                  className="editor-panel-icon-button"
                  aria-label="Minify JSON"
                >
                  <CompressIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {onExpand && (
            <Tooltip title="Expand Editor" arrow>
              <IconButton
                size="small"
                onClick={onExpand}
                className="editor-panel-icon-button"
                aria-label="Expand Editor"
              >
                <FullscreenIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {inactive && !value.trim() && inactiveHint ? (
        <Box
          className="editor-container"
          sx={{
            height: height,
            borderStyle: 'dashed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--surface)'
          }}
        >
          <Typography variant="body2" sx={{ color: 'var(--fg-muted)', px: 3, textAlign: 'center' }}>
            {inactiveHint}
          </Typography>
        </Box>
      ) : (
        <Box
          className="editor-container editor-container-shadow"
          sx={{
            height: height,
            opacity: inactive ? 0.6 : 1,
            pointerEvents: inactive ? 'none' : 'auto',
            transition: 'opacity 0.25s ease',
            ...(isDragOver && {
              outline: '2px dashed var(--accent)',
              outlineOffset: -2,
              backgroundColor: 'color-mix(in srgb, var(--accent) 8%, var(--bg-subtle))'
            }),
            ...(tinted && {
              backgroundColor: 'color-mix(in srgb, var(--accent) 5%, var(--bg-subtle))'
            })
          }}
        >
          <TextareaCodeEditor
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            placeholder={placeholder}
            isDarkMode={isDarkMode}
            forceDarkEditor={forceDarkEditor}
            forceDarkerBackground={forceDarkerBackground}
            ariaLabel={ariaLabel || (typeof label === 'string' ? label : undefined)}
          />
        </Box>
      )}
    </Box>
  );
};

export default EditorPanel;
